import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import {
  BOOKING_FORM_NO_TIME,
  bookingFieldDateKey,
  buildEndTimeIsoOptions,
  getCheckoutDatesFromEndOptions,
  isDateOnClosedScheduleDay,
  isUnsetTime,
  resolveAdvanceBookingLimitDays,
  resolveEffectiveMinBookingMinutes,
  resolveSlotStepMinutes
} from './bookingFormHelpers';

/**
 * Shared slot/calendar orchestration for AddBookingV2 and EditBookingV2 parents.
 */
export function useBookingDetailForm({
  amenity,
  availabilitySlots,
  watchStartTime,
  watchEndDate,
  watchStartDate,
  watchEndTime,
  watchAmenityId,
  watchBuilding,
  setValue,
  clearErrors,
  bookingId,
  isFormInitialized = true
}) {
  const allowMultiDayBooking = !!amenity?.allow_multi_day_bookings;
  const slotStepMinutes = resolveSlotStepMinutes(amenity);
  const minBookingMinutes = resolveEffectiveMinBookingMinutes(amenity);

  const selectedStartSlotRow = useMemo(() => {
    if (isUnsetTime(watchStartTime) || typeof watchStartTime !== 'string') return null;
    return availabilitySlots.find((s) => s.start_time === watchStartTime) ?? null;
  }, [availabilitySlots, watchStartTime]);

  const bookingMaxEndMoment = useMemo(() => {
    if (!selectedStartSlotRow?.max_end_time) return null;
    const m = moment.parseZone(selectedStartSlotRow.max_end_time);
    return m.isValid() ? m : null;
  }, [selectedStartSlotRow]);

  const allEndTimeOptions = useMemo(() => {
    if (isUnsetTime(watchStartTime) || typeof watchStartTime !== 'string') return [];

    const row = availabilitySlots.find((s) => s.start_time === watchStartTime);
    if (!row?.max_end_time) return [];

    return buildEndTimeIsoOptions(
      row.start_time,
      row.max_end_time,
      slotStepMinutes,
      minBookingMinutes
    );
  }, [availabilitySlots, minBookingMinutes, slotStepMinutes, watchStartTime]);

  const validEndDateKeys = useMemo(
    () => getCheckoutDatesFromEndOptions(allEndTimeOptions),
    [allEndTimeOptions]
  );

  const endTimeOptions = useMemo(() => {
    if (!allowMultiDayBooking) {
      return allEndTimeOptions;
    }

    const endYmd = bookingFieldDateKey(watchEndDate);
    if (!endYmd) {
      return [];
    }

    return allEndTimeOptions.filter(
      (iso) => moment.parseZone(iso).format('YYYY-MM-DD') === endYmd
    );
  }, [allowMultiDayBooking, allEndTimeOptions, watchEndDate]);

  const disabledDates = (current) => {
    if (amenity && isDateOnClosedScheduleDay(current, amenity.amenities_schedules)) {
      return true;
    }

    if (current && current.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
      return false;
    }

    const limitDays = resolveAdvanceBookingLimitDays(amenity);
    const maxBookable = moment().startOf('day').add(limitDays, 'days');
    if (current && current.startOf('day').isAfter(maxBookable)) {
      return true;
    }

    const today = moment().endOf('day');
    return current && current.isSameOrBefore(today);
  };

  const disabledEndDate = (current) => {
    if (!allowMultiDayBooking) {
      return true;
    }

    if (watchStartDate && current && moment(current).isBefore(moment(watchStartDate), 'day')) {
      return true;
    }

    if (isUnsetTime(watchStartTime) || typeof watchStartTime !== 'string') {
      return true;
    }

    if (!bookingMaxEndMoment?.isValid()) return true;

    const startDateMoment = moment(watchStartDate);
    const currentMoment = moment(current);
    const maxEndDayStart = bookingMaxEndMoment.clone().startOf('day');

    const isBeforeStartDate = currentMoment.isBefore(startDateMoment, 'day');
    const isAfterMaxEndDate = currentMoment.startOf('day').isAfter(maxEndDayStart);

    if (isBeforeStartDate || isAfterMaxEndDate) {
      return true;
    }

    if (validEndDateKeys.size > 0) {
      const ymd = current.format('YYYY-MM-DD');
      if (!validEndDateKeys.has(ymd)) {
        return true;
      }
    }

    return false;
  };

  const prevStartDate = useRef(watchStartDate);
  const prevStartTime = useRef(watchStartTime);
  const prevEndDate = useRef(watchEndDate);
  const prevAmenityId = useRef(watchAmenityId);
  const prevBuilding = useRef(watchBuilding);
  const wasFormInitializedRef = useRef(isFormInitialized);
  const isFirstRenderRef = useRef(true);

  // Sync refs while waiting for Edit form to hydrate so we don't cascade on mount
  useEffect(() => {
    // Initialize refs on first render or when form is not initialized (Edit mode)
    if (isFirstRenderRef.current || !isFormInitialized) {
      prevStartDate.current = watchStartDate;
      prevStartTime.current = watchStartTime;
      prevEndDate.current = watchEndDate;
      prevAmenityId.current = watchAmenityId;
      prevBuilding.current = watchBuilding;
      wasFormInitializedRef.current = isFormInitialized;
      isFirstRenderRef.current = false;
      return;
    }

    // One-time sync when Edit hydration completes (reset + isFormInitialized flip)
    if (!wasFormInitializedRef.current && isFormInitialized) {
      prevStartDate.current = watchStartDate;
      prevStartTime.current = watchStartTime;
      prevEndDate.current = watchEndDate;
      prevAmenityId.current = watchAmenityId;
      prevBuilding.current = watchBuilding;
      wasFormInitializedRef.current = true;
    }
  }, [isFormInitialized, watchStartDate, watchStartTime, watchEndDate, watchAmenityId, watchBuilding]);

  const clearCascadeErrors = (fields) => {
    if (clearErrors) clearErrors(fields);
  };

  // 0. Highest Level Change: Building (AddBooking only)
  useEffect(() => {
    if (!isFormInitialized) return;
    if (!watchBuilding) return; // Skip if building is not tracked (EditBooking case)

    const buildingChanged = 
      prevBuilding.current != null && 
      watchBuilding?.value !== prevBuilding.current?.value;

    if (buildingChanged) {
      // Building change clears amenity and all date/time fields
      setValue('amenity_id', undefined, { shouldValidate: false, shouldDirty: true });
      setValue('startDate', null, { shouldValidate: false, shouldDirty: true });
      setValue('startTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      setValue('endDate', null, { shouldValidate: false, shouldDirty: true });
      
      // Update refs to prevent cascade loops
      prevAmenityId.current = undefined;
      prevStartDate.current = null;
      prevStartTime.current = BOOKING_FORM_NO_TIME;
      prevEndDate.current = null;
      
      clearCascadeErrors(['amenity_id', 'startDate', 'startTime', 'endTime', 'endDate']);
    }

    prevBuilding.current = watchBuilding;
  }, [
    isFormInitialized,
    watchBuilding,
    setValue,
    clearErrors
  ]);

  // 1. Top Level Change: Amenity or Start Date
  useEffect(() => {
    if (!isFormInitialized) return;

    const amenityChanged =
      prevAmenityId.current != null && watchAmenityId !== prevAmenityId.current;
    const startDateKey = bookingFieldDateKey(watchStartDate);
    const prevStartDateKey = bookingFieldDateKey(prevStartDate.current);
    const startDateChanged = startDateKey !== prevStartDateKey;

    if (amenityChanged || startDateChanged) {
      // Clear dependent time fields
      setValue('startTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      prevStartTime.current = BOOKING_FORM_NO_TIME;

      // Clear end date for multi-day bookings or when amenity changes
      if (allowMultiDayBooking || amenityChanged) {
        setValue('endDate', null, { shouldValidate: false, shouldDirty: true });
        prevEndDate.current = null;
      }

      if (amenityChanged) {
        // For amenity changes, always clear start date to force re-selection
        // Different amenities may have different availability schedules
        setValue('startDate', null, { shouldValidate: false, shouldDirty: true });
        prevStartDate.current = null;
        clearCascadeErrors(['startTime', 'endTime', 'endDate', 'startDate']);
      } else {
        // For start date changes (including clearing), just clear dependent fields
        prevStartDate.current = watchStartDate;
        clearCascadeErrors(['startTime', 'endTime', 'endDate']);
      }

      prevAmenityId.current = watchAmenityId;
    }
  }, [
    isFormInitialized,
    watchStartDate,
    watchAmenityId,
    allowMultiDayBooking,
    setValue,
    clearErrors
  ]);

  // 2. Middle Level Change: Start Time or End Date
  useEffect(() => {
    if (!isFormInitialized) return;

    const startTimeChanged = watchStartTime !== prevStartTime.current;
    const endDateKey = bookingFieldDateKey(watchEndDate);
    const prevEndDateKey = bookingFieldDateKey(prevEndDate.current);
    const endDateChanged =
      endDateKey !== prevEndDateKey &&
      (endDateKey !== '' || prevEndDateKey !== '');

    if (startTimeChanged || endDateChanged) {
      // Always clear end time when start time or end date changes
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldValidate: false, shouldDirty: true });
      
      // Clear errors for affected fields
      if (startTimeChanged) {
        clearCascadeErrors(['endTime', 'startTime']);
      } else {
        clearCascadeErrors(['endTime']);
      }

      let wipedEndDate = false;
      if (startTimeChanged && watchEndDate && bookingMaxEndMoment) {
        // Safety net: Check if the new start time makes the currently selected end date impossible
        if (moment(watchEndDate).startOf('day').isAfter(bookingMaxEndMoment, 'day')) {
          setValue('endDate', null, { shouldValidate: false, shouldDirty: true });
          clearCascadeErrors(['endDate']);
          wipedEndDate = true;
        }
      }

      prevStartTime.current = watchStartTime;
      prevEndDate.current = wipedEndDate ? null : watchEndDate;
    }
  }, [
    isFormInitialized,
    watchStartTime,
    watchEndDate,
    bookingMaxEndMoment,
    setValue,
    clearErrors
  ]);

  // 3. Max Boundary Check: End Date
  useEffect(() => {
    if (!isFormInitialized) return;
    if (!allowMultiDayBooking || !bookingMaxEndMoment?.isValid() || !watchEndDate) return;
    if (moment(watchEndDate).isAfter(bookingMaxEndMoment, 'day')) {
      setValue('endDate', bookingMaxEndMoment.clone(), {
        shouldDirty: true,
        shouldValidate: false
      });
      clearCascadeErrors('endDate');
    }
  }, [
    isFormInitialized,
    allowMultiDayBooking,
    bookingMaxEndMoment,
    watchEndDate,
    setValue,
    clearErrors
  ]);

  // 4. Max Boundary Check: End Time
  useEffect(() => {
    if (!isFormInitialized) return;
    if (!bookingMaxEndMoment?.isValid()) return;
    if (isUnsetTime(watchEndTime) || typeof watchEndTime !== 'string') return;
    const endM = moment.parseZone(watchEndTime);
    if (!endM.isValid()) return;
    if (endM.isAfter(bookingMaxEndMoment)) {
      setValue('endTime', BOOKING_FORM_NO_TIME, { shouldDirty: true, shouldValidate: false });
      clearCascadeErrors('endTime');
    }
  }, [
    isFormInitialized,
    bookingMaxEndMoment,
    setValue,
    watchEndTime,
    watchStartTime,
    clearErrors
  ]);

  return {
    allowMultiDayBooking,
    slotStepMinutes,
    minBookingMinutes,
    selectedStartSlotRow,
    bookingMaxEndMoment,
    endTimeOptions,
    disabledDates,
    disabledEndDate
  };
};
